import { button, icon } from '../../tokens/component';
import { state } from '../../tokens/semantic';
import { color as foundationColor } from '../../tokens/foundation';

const MuiIconButton = {
    defaultProps: {
        disableFocusRipple: true,
        disableRipple: true,
        variant: 'contained',
        size: 'medium',
    },
    styleOverrides: {
        root: ({ theme }) => ({
            borderRadius: button.size.radius.icon,
            '&:focus-visible': {
                outline: `${state.outline.weight.focus.md} solid ${state.outline.color.focus}`,
                outlineOffset: state.outline.offset.focus.md,
                boxShadow: 'inherit'
            },
            '&.Mui-disabled': {
                opacity: state.opacity.disabled,
                boxShadow: 'none'
            },
            '&&': {
                [theme.breakpoints.down('sm')]: {
                    width: button.size.lg,
                    height: button.size.lg,
                    '& svg, & .MuiSvgIcon-root': {
                        width: icon.size.lg,
                        height: icon.size.lg,
                    },
                },
            },
        }),
        sizeSmall: {
            width: button.size.sm,
            height: button.size.sm,
            '& svg, & .MuiSvgIcon-root': {
                width: icon.size.sm,
                height: icon.size.sm,
            }
        },
        sizeMedium: {
            width: button.size.md,
            height: button.size.md,
            '& svg, & .MuiSvgIcon-root': {
                width: icon.size.md,
                height: icon.size.md,
            }
        },
        sizeLarge: {
            width: button.size.lg,
            height: button.size.lg,
            '& svg, & .MuiSvgIcon-root': {
                width: icon.size.lg,
                height: icon.size.lg,
            },
        },
        colorPrimary: {
            backgroundColor: button.color.filled.bg.primary.default,
            color: foundationColor.neutral.white,
            '&:hover': {
                backgroundColor: button.color.filled.bg.primary.hover,
            },
            '&:active': {
                backgroundColor: button.color.filled.bg.primary.active,
            },
            '&.Mui-disabled': {
                backgroundColor: button.color.filled.bg.primary.default,
                color: button.color.filled.content.default,
            },
            boxShadow: button.effects.shadow.primary.default
        },
        colorSecondary: {
            backgroundColor: button.color.filled.bg.secondary.default,
            border: `1px solid ${button.color.filled.border.secondary}`,
            color: button.color.filled.content.secondary,
            '&:hover': {
                backgroundColor: button.color.filled.bg.secondary.hover,
            },
            '&:active': {
                backgroundColor: button.color.filled.bg.secondary.active,
            },
            '&.Mui-disabled': {
                backgroundColor: button.color.filled.bg.secondary.disabled,
                color: button.color.filled.content.secondary,
            },
            boxShadow: button.effects.shadow.secondary.default
        },
        colorError: {
            backgroundColor: button.color.filled.bg.destructive.default,
            color: foundationColor.neutral.white,
            '&:hover': {
                backgroundColor: button.color.filled.bg.destructive.hover,
            },
            '&:active': {
                backgroundColor: button.color.filled.bg.destructive.active,
            },
            '&.Mui-disabled': {
                backgroundColor: button.color.filled.bg.destructive.default,
                color: button.color.filled.content.default,
            },
            boxShadow: button.effects.shadow.destructive.default
        },
        colorSuccess: {
            backgroundColor: button.color.filled.bg.accent.default,
            color: foundationColor.neutral.white,
            '&:hover': {
                backgroundColor: button.color.filled.bg.accent.hover,
            },
            '&:active': {
                backgroundColor: button.color.filled.bg.accent.active,
            },
            '&.Mui-disabled': {
                backgroundColor: button.color.filled.bg.accent.default,
                color: button.color.filled.content.default,
            },
            boxShadow: button.effects.shadow.accent.default
        },
        colorWarning: {
            backgroundColor: button.color.filled.bg.warning.default,
            color: foundationColor.neutral.white,
            '&:hover': {
                backgroundColor: button.color.filled.bg.warning.hover,
            },
            '&:active': {
                backgroundColor: button.color.filled.bg.warning.active,
            },
            '&.Mui-disabled': {
                backgroundColor: button.color.filled.bg.warning.default,
                color: button.color.filled.content.default,
            },
            boxShadow: button.effects.shadow.warning.default
        },
    },
    variants: [
        {
            props: { variant: 'ghost', color: 'primary' },
            style: {
                backgroundColor: 'transparent',
                color: button.color.ghost.content.primary,
                border: 'none',
                boxShadow: 'none',
                '&:hover': {
                    backgroundColor: button.color.ghost.bg.hover,
                },
                '&:active': {
                    backgroundColor: button.color.ghost.bg.active,
                },
                '&.Mui-disabled': {
                    backgroundColor: 'transparent',
                    color: button.color.ghost.content.primary,
                }
            },
        },
        {
            props: { variant: 'ghost', color: 'secondary' },
            style: {
                backgroundColor: 'transparent',
                color: button.color.ghost.content.secondary,
                border: 'none',
                boxShadow: 'none',
                '&:hover': {
                    backgroundColor: button.color.ghost.bg.hover,
                },
                '&:active': {
                    backgroundColor: button.color.ghost.bg.active,
                },
                '&.Mui-disabled': {
                    backgroundColor: 'transparent',
                    color: button.color.ghost.content.secondary,
                }
            },
        },
        {
            props: { variant: 'ghost', color: 'error' },
            style: {
                backgroundColor: 'transparent',
                color: button.color.ghost.content.destructive,
                border: 'none',
                boxShadow: 'none',
                '&:hover': {
                    backgroundColor: button.color.ghost.bg.hover,
                },
                '&:active': {
                    backgroundColor: button.color.ghost.bg.active,
                },
                '&.Mui-disabled': {
                    backgroundColor: 'transparent',
                    color: button.color.ghost.content.destructive,
                }
            },
        },
        {
            props: { variant: 'ghost', color: 'success' },
            style: {
                backgroundColor: 'transparent',
                color: button.color.ghost.content.accent,
                border: 'none',
                boxShadow: 'none',
                '&:hover': {
                    backgroundColor: button.color.ghost.bg.hover,
                },
                '&:active': {
                    backgroundColor: button.color.ghost.bg.active,
                },
                '&.Mui-disabled': {
                    backgroundColor: 'transparent',
                    color: button.color.ghost.content.accent,
                }
            },
        },
        {
            props: { variant: 'ghost', color: 'warning' },
            style: {
                backgroundColor: 'transparent',
                color: button.color.ghost.content.warning,
                border: 'none',
                boxShadow: 'none',
                '&:hover': {
                    backgroundColor: button.color.ghost.bg.hover,
                },
                '&:active': {
                    backgroundColor: button.color.ghost.bg.active,
                },
                '&.Mui-disabled': {
                    backgroundColor: 'transparent',
                    color: button.color.ghost.content.warning,
                }
            },
        },
    ],
};

export default MuiIconButton;